import React, { useEffect, useMemo, useRef } from 'react';
import { DraftFunction } from 'use-immer';
import { LayerData } from './Layer.types';
import { CurrentSelectionStrategy } from '../steps/CardDesign/SelectionStrategy';
import { useSurface } from '../steps/CardDesign/useSurface';
import { ScopedUpdateHandler } from '../useCardCustomization';
import { Page, PageEditor, PageProps } from './Page';
import { Box } from '@withjoy/joykit';
import { XY, startDrag } from '../steps/CardDesign/startDrag';
import { calcSafeMargin } from '../steps/CardDesign/Adornments';
import { useCardCustomizerTelemetry } from '../CardCustomizer.telemetry';
import { clamp } from '@apps/card/utils';
import { getRotatedBounds } from '../steps/CardDesign/Adornments.math';
import { PPI } from '../steps/CardDesign/config';

type UpdatePage = ScopedUpdateHandler<{
  fill: string;
  layers: LayerData[];
}>;

/**
 * Increments that dragging should snap to
 */
const DRAG_GRID: [number, number] = [2, 2];

function snapToGrid(grid: [number, number], pendingX: number, pendingY: number) {
  const x = Math.round(pendingX / grid[0]) * grid[0];
  const y = Math.round(pendingY / grid[1]) * grid[1];
  return [x, y];
}

export const EditablePage = (props: PageProps & { update: UpdatePage }) => {
  const { update, ...pageProps } = props;

  const { customizationButtonInteracted } = useCardCustomizerTelemetry();

  const [surface, updateSurface] = useSurface();

  // Every time the active layer changes, clear the editing layer.
  useEffect(() => {
    updateSurface(draft => {
      draft.editingLayerIndex = undefined;
    });
  }, [surface.activeLayerIndex, updateSurface]);

  // Storing surface in a ref so that it's not required as a dependency for the useMemo below.
  // `surface` changes but we need `editor` to be stable for downstream consumers.
  const surfaceRef = useRef(surface);
  surfaceRef.current = surface;

  const editor: PageEditor = useMemo(
    () => ({
      isActive: (layerIndex: number) => {
        return surfaceRef.current.activeLayerIndex === layerIndex;
      },
      isEditing: (layerIndex: number) => {
        return CurrentSelectionStrategy.isEditing(surfaceRef.current.editingLayerIndex, layerIndex);
      },
      updateLayer: (layerIndex: number, scopedUpdater: DraftFunction<LayerData>) => {
        update(draft => {
          const layer = draft.layers[layerIndex];
          if (!layer) return;
          scopedUpdater(layer);
        });
      },
      Selector: ({ children, layer, layerIndex }) => {
        const { cardLayerFocused } = useCardCustomizerTelemetry();

        const onDrag = (delta: XY, baseline: { x: number; y: number; width: number; height: number }) => {
          editor.updateLayer(layerIndex, draft => {
            const dimensions = pageProps.page.dimensions ?? pageProps;
            const [x, y] = snapToGrid(DRAG_GRID, baseline.x + delta.x / surfaceRef.current.scale, baseline.y + delta.y / surfaceRef.current.scale);
            const margin = calcSafeMargin(pageProps.page.dimensions);
            const bounds = getRotatedBounds(baseline);
            draft.layout.x = clamp(margin - bounds.x, x, -(bounds.width + bounds.x) + -margin + dimensions.width * PPI);
            draft.layout.y = clamp(margin - bounds.y, y, -(bounds.height + bounds.y) + -margin + dimensions.height * PPI);
          });
        };

        return (
          <Box
            display="contents"
            pointerEvents={layer.editable ? 'all' : 'none'}
            onPointerOver={e => {
              if (e.button >= 0) return;
              updateSurface(draftSurface => {
                draftSurface.hoverLayerIndex = layerIndex;
              });
            }}
            onPointerOut={e => {
              if (e.button >= 0) return;
              updateSurface(draftSurface => {
                draftSurface.hoverLayerIndex = undefined;
              });
            }}
            onPointerDown={e => {
              if (!e.isPrimary) return;

              e.stopPropagation(); // Prevent deselection

              // Prevent browser native drag/drop...
              // Except when text layer is in edit mode (to let the caret placement through).
              // Except when image layer is in active mode (to allow panning).
              const isActive = surfaceRef.current.activeLayerIndex === layerIndex;
              const isEditing = surfaceRef.current.editingLayerIndex === layerIndex;
              const isImageLayerActive = layer.type === 'image' && isActive;
              if (!(isEditing || isImageLayerActive)) {
                e.preventDefault();
                // De-focus any formatting toolbar text boxes (such as font size) when switching between selected layers.
                void (document.activeElement as HTMLElement).blur();
              }

              if (!isActive) {
                startDrag(
                  () => {},
                  undefined,
                  didDrag => {
                    if (didDrag) return;
                    updateSurface(draftSurface => {
                      draftSurface.activeLayerIndex = layerIndex;
                    });
                    cardLayerFocused(layer.type);
                  },
                )(e);
                return;
              }

              // Disallow move/drag while editing text
              if (isEditing) return;

              // Disallow move/drag for images.
              if (layer.type === 'image') return;

              startDrag(onDrag, JSON.parse(JSON.stringify(layer.layout)), didDrag => {
                if (didDrag) return;
                if (layer.type !== 'text') return;
                updateSurface(draftSurface => {
                  // If this layer received the onPointerDown, then activeLayerIndex === layerIndex at this point.
                  draftSurface.editingLayerIndex = surfaceRef.current.activeLayerIndex;
                  customizationButtonInteracted({
                    property: 'textLayer_editText',
                  });
                });
              })(e);
            }}
          >
            {children}
          </Box>
        );
      },
      updateSurface,
    }),
    // Not including `update, surface, updateSurface` as it is unstable and invalidates this memo on every hover.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Page
      {...pageProps}
      editor={editor}
    />
  );
};
